import React from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { Helmet } from "react-helmet";
import styles from "./Service.module.css";
import { Link } from 'react-router-dom';
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 },
  },
};
const MotionLink = motion(Link);
const itemVariants = {
  hidden: { opacity: 0, x: -60 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.7, type: "spring" } },
};

const Service = () => {
  const { scrollYProgress } = useScroll();
  const parallaxY = useTransform(scrollYProgress, [0, 1], [0, -150]);
  const springParallax = useSpring(parallaxY, { stiffness: 120, damping: 25 });

  return (
    <>
      <Helmet>
        <title>Wedding Photography & Videography Services | Studio Maamangala</title>
        <meta
          name="description"
          content="Studio Maamangala offers cinematic wedding photography and videography in Odisha, blending tradition with modern artistry to capture your love story."
        />
        <meta
          name="keywords"
          content="wedding photography Odisha, cinematic videography, Indian wedding cinematographer, Studio Maamangala, pre-wedding shoots"
        />
        <meta property="og:title" content="Wedding Services | Studio Maamangala" />
        <meta
          property="og:description"
          content="Transform your wedding into a cinematic masterpiece with our photography, videography, and storytelling services."
        />
        <meta property="og:url" content="https://studiomaamangala.com/services" />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="https://studiomaamangala.com/wp-content/uploads/2025/06/dnk-photo-8xSkPxFARP4-unsplash-scaled.jpg"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Wedding Photography & Videography | Studio Maamangala" />
        <meta name="twitter:description" content="Capture your love story with cinematic visuals by Studio Maamangala in Odisha." />
        <meta
          name="twitter:image"
          content="https://studiomaamangala.com/wp-content/uploads/2025/06/dnk-photo-8xSkPxFARP4-unsplash-scaled.jpg"
        />
      </Helmet>

      <motion.div className={styles.servicePage} initial="hidden" animate="visible" variants={containerVariants}>
        {/* Hero Section */}
        <section className={styles.hero}>
          <motion.div
            className={styles.heroOverlay}
            style={{ y: springParallax }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.85 }}
            transition={{ duration: 2 }}
          />
          <motion.img
            src="https://studiomaamangala.com/wp-content/uploads/2025/06/dnk-photo-8xSkPxFARP4-unsplash-scaled.jpg"
            alt="Wedding Scene"
            className={styles.heroImage}
            style={{ y: springParallax }}
            initial={{ scale: 1.3 }}
            animate={{ scale: 1 }}
            transition={{ duration: 2.5, type: "spring" }}
          />
          <motion.div className={styles.heroContent} variants={containerVariants}>
            <motion.h1 variants={itemVariants}>Cinematic Wedding Services</motion.h1>
            <motion.p variants={itemVariants}>
              Crafting <strong>timeless love stories</strong> through photography and videography in Odisha.
            </motion.p>
            <MotionLink
              to="/contact"
              className={styles.ctaButton}
              variants={itemVariants}
              whileHover={{ scale: 1.15, boxShadow: "0 0 20px rgba(212, 160, 23, 0.6)" }}
              whileTap={{ scale: 0.85 }}
            >
              Book Now
            </MotionLink>
          </motion.div>
        </section>

        {/* Services Section */}
        <motion.section
          className={styles.servicesSection}
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <motion.div className={styles.servicesContent}>
            <motion.h2 variants={itemVariants}>Our Services</motion.h2>
            <div className={styles.servicesGrid}>
              {[
                {
                  title: "Cinematic Wedding Videography",
                  desc: "High-definition films that capture every emotion with cinematic flair.",
                },
                {
                  title: "Artistic Wedding Photography",
                  desc: "Stunning images blending traditional and modern aesthetics.",
                },
                {
                  title: "Pre-Wedding Shoots",
                  desc: "Romantic save-the-date films and photos in breathtaking locations.",
                },
                {
                  title: "Drone Coverage",
                  desc: "Aerial visuals for grand, sweeping perspectives of your day.",
                },
                {
                  title: "Candid Storytelling",
                  desc: "Authentic moments captured with emotional depth.",
                },
                {
                  title: "Same-Day Edits",
                  desc: "Instant highlights and Instagram-ready clips for your celebration.",
                },
              ].map((service, index) => (
                <motion.div
                  key={index}
                  className={styles.serviceCard}
                  variants={itemVariants}
                  whileHover={{ y: -10, rotate: index % 2 ? 2 : -2, boxShadow: "0 15px 30px rgba(212, 160, 23, 0.4)" }}
                >
                  <h3>{service.title}</h3>
                  <p>{service.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </motion.section>

        {/* Approach Section */}
        <motion.section
          className={styles.approachSection}
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <motion.div className={styles.approachContent}>
            <motion.h2 variants={itemVariants}>Our Approach</motion.h2>
            <motion.p variants={itemVariants}>
              At Studio Maamangala, we blend artistry, technology, and emotion to create wedding visuals that resonate. Our process begins with understanding your story, followed by meticulous planning and execution using state-of-the-art equipment.
            </motion.p>
            <motion.p variants={itemVariants}>
              We collaborate closely with you to ensure every moment is captured authentically, delivering a cinematic experience that reflects your unique love story.
            </motion.p>
          </motion.div>
        </motion.section>

        {/* Packages Section */}
        <motion.section
          className={styles.packagesSection}
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <motion.div className={styles.packagesContent}>
            <motion.h2 variants={itemVariants}>Signature Packages</motion.h2>
            <div className={styles.packagesGrid}>
              {[
                {
                  title: "Essential",
                  desc: "Core photography and videography for intimate weddings.",
                },
                {
                  title: "Premium",
                  desc: "Comprehensive coverage with drone shots and candid storytelling.",
                },
                {
                  title: "Cinematic",
                  desc: "All-inclusive package with pre-wedding shoots and same-day edits.",
                },
              ].map((pkg, index) => (
                <motion.div
                  key={index}
                  className={styles.packageCard}
                  variants={itemVariants}
                  whileHover={{ scale: 1.05, boxShadow: "0 10px 20px rgba(212, 160, 23, 0.3)" }}
                >
                  <h3>{pkg.title}</h3>
                  <p>{pkg.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </motion.section>

        {/* Impact Section */}
        <motion.section
          className={styles.impactSection}
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <motion.div className={styles.impactContent}>
            <motion.h2 variants={itemVariants}>Our Impact</motion.h2>
            <div className={styles.impactGrid}>
              <motion.div className={styles.impactCard} variants={itemVariants} whileHover={{ y: -10 }}>
                <h3>350+ Weddings</h3>
                <p>Serving couples across Odisha with love and dedication.</p>
              </motion.div>
              <motion.div className={styles.impactCard} variants={itemVariants} whileHover={{ y: -10 }}>
                <h3>Award-Winning</h3>
                <p>Odisha Wedding Photography Award 2023 for innovation.</p>
              </motion.div>
              <motion.div className={styles.impactCard} variants={itemVariants} whileHover={{ y: -10 }}>
                <h3>Client Trust</h3>
                <p>“A cinematic masterpiece that captured our love perfectly.” – Client, 2024</p>
              </motion.div>
            </div>
          </motion.div>
        </motion.section>
      </motion.div>
    </>
  );
};

export default Service;